/**
 * Authentication Data Access Layer
 * 
 * Provides authentication and authorization operations for the doctor portal.
 * Handles sign in, sign up, session management, and permission checks.
 */

import { supabase } from '../client';
import type { 
  Database,
  DoctorProfilesRow,
  DoctorInvitationsRow
} from '@/types/database.types';
import type { 
  AuthUser, 
  DoctorProfile, 
  SessionInfo 
} from '../client';

/**
 * Decode JWT token to extract custom claims
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

// Auth interfaces
interface SignInCredentials {
  email: string;
  password: string;
}

interface DoctorProfileData {
  fullName: string;
  medicalLicense: string;
  specialization?: string[];
  licenseExpiry?: string;
  practiceName?: string;
  practiceAddress?: string;
  practicePhone?: string;
  country?: string;
}

interface PasswordResetData {
  email: string;
}

interface PasswordUpdateData {
  password: string;
  accessToken?: string;
  refreshToken?: string;
}

interface InvitationAcceptanceData {
  token: string;
  password: string;
  specialization?: string[];
}

/**
 * Sign in with email and password
 */
export async function signIn(
  credentials: SignInCredentials
): Promise<{ 
  data: {
    user: AuthUser;
    session: any;
    profile: DoctorProfile | null;
  } | null; 
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (error) {
      // Authentication error handled
      return { data: null, error: new Error(error.message) };
    }

    if (!data.user || !data.session) {
      return { data: null, error: new Error('Authentication failed') };
    }

    // Decode the JWT to get the user_role custom claim
    let userRole = null;
    if (data.session.access_token) {
      const decodedToken = decodeJWT(data.session.access_token);
      userRole = decodedToken?.user_role || null;
    }

    // Fallback to app_metadata if JWT decode fails
    if (!userRole) {
      userRole = data.user.app_metadata?.role || null;
    }

    // Get user info
    const user: AuthUser = {
      id: data.user.id,
      email: data.user.email || null,
      role: userRole as 'doctor' | 'doctor_pending' | 'admin' | null,
      emailConfirmed: data.user.email_confirmed_at !== null,
      createdAt: data.user.created_at,
      lastSignInAt: data.user.last_sign_in_at || null,
    };

    // Get doctor profile if the user is a doctor or doctor_pending
    let profile: DoctorProfile | null = null;
    if (user.role === 'doctor' || user.role === 'doctor_pending') {
      const { data: profileData } = await supabase
        .from('doctor_profiles')
        .select('*')
        .eq('auth_user_id', data.user.id)
        .single();
      
      profile = profileData;
    }

    return {
      data: {
        user,
        session: data.session,
        profile,
      },
      error: null
    };
  } catch (error) {
    // Unexpected authentication error handled
    return { data: null, error: error as Error };
  }
}

/**
 * Sign out user
 */
export async function signOut(): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      // Sign out error handled
      return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (error) {
    // Unexpected sign out error handled
    return { error: error as Error };
  }
}

/**
 * Sign up new doctor - Step 1: Create auth account only
 */
export async function signUp(
  email: string,
  password: string
): Promise<{ 
  data: {
    userId: string;
    session: any;
    needsEmailConfirmation: boolean;
  } | null; 
  error: Error | null;
}> {
  console.log('SignUp called with email:', email);
  try {
    // Create auth user only - no profile or role yet
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/email-confirmation`,
      },
    });

    if (error) {
      console.error('Signup error:', error);
      if (error.message?.includes('already registered')) {
        return { data: null, error: new Error('This email is already registered. Please login or reset your password.') };
      }
      return { data: null, error: new Error(error.message || 'Failed to create account') };
    }

    if (!data.user) {
      return { data: null, error: new Error('User creation failed') };
    }

    // Auto sign-in after signup
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (signInError) {
      console.error('Auto sign-in error:', signInError);
    }

    return {
      data: {
        userId: data.user.id,
        session: signInData?.session || data.session,
        needsEmailConfirmation: !data.user.email_confirmed_at,
      },
      error: null
    };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Setup doctor profile - Step 2: Create profile after auth signup
 */
export async function setupDoctorProfile(
  profileData: DoctorProfileData
): Promise<{ 
  data: {
    profileId: string;
    practiceId?: string;
  } | null; 
  error: Error | null;
}> {
  console.log('Setting up doctor profile:', profileData);
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      return { data: null, error: new Error('Not authenticated') };
    }

    // Use the RPC function to create user and doctor profile with proper permissions
    // We'll skip creating medical practice for now - admin can assign or doctor can add later
    const { data, error: rpcError } = await supabase.rpc('post_signup_doctor', {
      p_name: profileData.fullName,
      p_medical_license: profileData.medicalLicense,
      p_license_expiry: profileData.licenseExpiry,
      p_specialization: profileData.specialization ? [profileData.specialization] : null,
      p_practice_id: null // No practice for now
    });

    if (rpcError) {
      console.error('Error calling post_signup_doctor RPC:', rpcError);
      return { data: null, error: new Error(rpcError.message || 'Failed to create profile') };
    }

    // Practice details will be handled by admin during approval process
    // For now, we just note them in the console for debugging
    if (profileData.practiceName || profileData.practiceAddress) {
      console.log('Practice details provided (to be created by admin):', {
        name: profileData.practiceName,
        address: profileData.practiceAddress,
        phone: profileData.practicePhone,
        country: profileData.country
      });
    }

    // Get the created doctor profile ID
    const { data: profileRecord, error: profileError } = await supabase
      .from('doctor_profiles')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    if (profileError || !profileRecord) {
      console.error('Error fetching doctor profile:', profileError);
      // Profile was likely created, return user.id as profileId
      return {
        data: {
          profileId: user.id,
          practiceId: undefined
        },
        error: null
      };
    }

    return {
      data: {
        profileId: profileRecord.id,
        practiceId: undefined
      },
      error: null
    };
  } catch (error) {
    console.error('Profile setup error:', error);
    return { data: null, error: error as Error };
  }
}

/**
 * Accept doctor invitation and create account
 */
export async function acceptInvitation(
  acceptanceData: InvitationAcceptanceData
): Promise<{ 
  data: {
    user: AuthUser;
    session: any;
    profile: DoctorProfile;
  } | null; 
  error: Error | null;
}> {
  try {
    // Validate invitation token
    const { data: invitation, error: invitationError } = await supabase
      .from('doctor_invitations')
      .select('*')
      .eq('invitation_token', acceptanceData.token)
      .is('accepted_at', null)
      .is('rejected_at', null)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (invitationError || !invitation) {
      return { 
        data: null, 
        error: new Error('Invalid or expired invitation token') 
      };
    }

    // Create auth user
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: invitation.email,
      password: acceptanceData.password,
      options: {
        data: {
          full_name: invitation.full_name,
          medical_license: invitation.medical_license,
          role: 'doctor',
        },
      },
    });

    if (signUpError || !signUpData.user) {
      // User account creation error handled
      return { data: null, error: new Error(signUpError?.message || 'Account creation failed') };
    }

    // Create doctor profile
    const { data: profile, error: profileError } = await supabase
      .from('doctor_profiles')
      .insert({
        auth_user_id: signUpData.user.id,
        full_name: invitation.full_name,
        medical_license: invitation.medical_license,
        specialization: acceptanceData.specialization ? JSON.stringify(acceptanceData.specialization) : null,
        prescribing_enabled: false, // Requires admin verification
        require_2fa: false,
      })
      .select()
      .single();

    if (profileError) {
      // Doctor profile creation error handled
      return { data: null, error: new Error(profileError.message) };
    }

    // Mark invitation as accepted
    await supabase
      .from('doctor_invitations')
      .update({
        accepted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', invitation.id);

    const user: AuthUser = {
      id: signUpData.user.id,
      email: signUpData.user.email || null,
      role: 'doctor',
      emailConfirmed: signUpData.user.email_confirmed_at !== null,
      createdAt: signUpData.user.created_at,
      lastSignInAt: signUpData.user.last_sign_in_at || null,
    };

    return {
      data: {
        user,
        session: signUpData.session,
        profile,
      },
      error: null
    };
  } catch (error) {
    // Unexpected invitation acceptance error handled
    return { data: null, error: error as Error };
  }
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<{ 
  data: AuthUser | null; 
  error: Error | null;
}> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return { data: null, error: error ? new Error(error.message) : null };
    }

    const authUser: AuthUser = {
      id: user.id,
      email: user.email || null,
      role: ((user as any).user_role || user.app_metadata?.role) as 'doctor' | 'doctor_pending' | 'admin' | null,
      emailConfirmed: user.email_confirmed_at !== null,
      createdAt: user.created_at,
      lastSignInAt: user.last_sign_in_at || null,
    };

    return { data: authUser, error: null };
  } catch (error) {
    // Unexpected error getting current user handled
    return { data: null, error: error as Error };
  }
}

/**
 * Get current session with user and profile info
 */
export async function getCurrentSession(): Promise<{ 
  data: SessionInfo | null; 
  error: Error | null;
}> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      return { data: null, error: error ? new Error(error.message) : null };
    }

    // Decode the JWT to get the user_role custom claim
    let userRole = null;
    if (session.access_token) {
      const decodedToken = decodeJWT(session.access_token);
      userRole = decodedToken?.user_role || null;
    }

    // Fallback to app_metadata if JWT decode fails
    if (!userRole) {
      userRole = session.user.app_metadata?.role || null;
    }

    const user: AuthUser = {
      id: session.user.id,
      email: session.user.email || null,
      role: userRole as 'doctor' | 'doctor_pending' | 'admin' | null,
      emailConfirmed: session.user.email_confirmed_at !== null,
      createdAt: session.user.created_at,
      lastSignInAt: session.user.last_sign_in_at || null,
    };

    // Get doctor profile if the user is a doctor or doctor_pending
    let profile: DoctorProfile | null = null;
    if (user.role === 'doctor' || user.role === 'doctor_pending') {
      const { data: profileData } = await supabase
        .from('doctor_profiles')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .single();
      
      profile = profileData;
    }

    return {
      data: { session, user, profile },
      error: null
    };
  } catch (error) {
    // Unexpected session error handled
    return { data: null, error: error as Error };
  }
}

/**
 * Refresh session
 */
export async function refreshSession(): Promise<{ 
  data: { session: any | null; user: AuthUser | null } | null; 
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      // Session refresh error handled
      return { data: null, error: new Error(error.message) };
    }

    if (!data.session || !data.user) {
      return { data: { session: null, user: null }, error: null };
    }

    const user: AuthUser = {
      id: data.user.id,
      email: data.user.email || null,
      role: ((data.user as any).user_role || data.user.app_metadata?.role) as 'doctor' | 'doctor_pending' | 'admin' | null,
      emailConfirmed: data.user.email_confirmed_at !== null,
      createdAt: data.user.created_at,
      lastSignInAt: data.user.last_sign_in_at || null,
    };

    return {
      data: { session: data.session, user },
      error: null
    };
  } catch (error) {
    // Unexpected session refresh error handled
    return { data: null, error: error as Error };
  }
}

/**
 * Check if user has a specific role
 */
export async function checkUserRole(
  role: 'doctor' | 'admin'
): Promise<{ hasRole: boolean; error: Error | null }> {
  try {
    const { data: user, error } = await getCurrentUser();
    
    if (error || !user) {
      return { hasRole: false, error: error || new Error('User not authenticated') };
    }

    return { hasRole: user.role === role, error: null };
  } catch (error) {
    // Unexpected user role check error handled
    return { hasRole: false, error: error as Error };
  }
}

/**
 * Check doctor permissions (can prescribe, verified, etc.)
 */
export async function checkDoctorPermissions(): Promise<{ 
  data: {
    isDoctor: boolean;
    isVerified: boolean;
    canPrescribe: boolean;
    isSuspended: boolean;
    profile: DoctorProfile | null;
  } | null; 
  error: Error | null;
}> {
  try {
    const { data: user, error: userError } = await getCurrentUser();
    
    if (userError || !user) {
      return { data: null, error: userError || new Error('User not authenticated') };
    }

    if (user.role !== 'doctor') {
      return {
        data: {
          isDoctor: false,
          isVerified: false,
          canPrescribe: false,
          isSuspended: false,
          profile: null,
        },
        error: null
      };
    }

    // Get doctor profile
    const { data: profile, error: profileError } = await supabase
      .from('doctor_profiles')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (profileError) {
      // Doctor profile fetch error handled
      return { data: null, error: new Error(profileError.message) };
    }

    const isVerified = profile.verified_at !== null;
    const isSuspended = profile.suspended_at !== null;
    const licenseValid = profile.license_expiry ? new Date(profile.license_expiry) > new Date() : true;
    const canPrescribe = profile.prescribing_enabled && isVerified && !isSuspended && licenseValid;

    return {
      data: {
        isDoctor: true,
        isVerified,
        canPrescribe,
        isSuspended,
        profile,
      },
      error: null
    };
  } catch (error) {
    // Unexpected doctor permissions error handled
    return { data: null, error: error as Error };
  }
}

/**
 * Request password reset
 */
export async function requestPasswordReset(
  resetData: PasswordResetData
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(resetData.email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      // Password reset request error handled
      return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (error) {
    // Unexpected password reset error handled
    return { error: error as Error };
  }
}

/**
 * Update password
 */
export async function updatePassword(
  updateData: PasswordUpdateData
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: updateData.password,
    });

    if (error) {
      // Password update error handled
      return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (error) {
    // Unexpected password update error handled
    return { error: error as Error };
  }
}

/**
 * Validate invitation token
 */
export async function validateInvitationToken(
  token: string
): Promise<{ 
  data: DoctorInvitationsRow | null; 
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase
      .from('doctor_invitations')
      .select('*')
      .eq('invitation_token', token)
      .is('accepted_at', null)
      .is('rejected_at', null)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error) {
      // Invitation token validation error handled
      return { data: null, error: new Error('Invalid or expired invitation token') };
    }

    return { data, error: null };
  } catch (error) {
    // Unexpected invitation validation error handled
    return { data: null, error: error as Error };
  }
}

/**
 * Helper function to create doctor profile after sign up confirmation
 */
async function createDoctorProfile(
  authUserId: string,
  profileData: DoctorProfileData
): Promise<{ practiceId?: string }> {
  try {
    // First, create medical practice if practice name is provided
    let practiceId: string | null = null;
    if (profileData.practiceName) {
      // Check if practice already exists
      const { data: existingPractice } = await supabase
        .from('medical_practices')
        .select('id')
        .eq('name', profileData.practiceName)
        .single();

      if (existingPractice) {
        practiceId = existingPractice.id;
      } else {
        // Create new practice
        const { data: newPractice, error: practiceError } = await supabase
          .from('medical_practices')
          .insert({
            name: profileData.practiceName,
            address: profileData.practiceAddress || null,
            phone: profileData.practicePhone || null,
            email: profileData.email,
            country: profileData.country || null,
            eprescribe_enabled: true,
          })
          .select('id')
          .single();

        if (practiceError) {
          throw new Error(`Failed to create practice: ${practiceError.message}`);
        }
        practiceId = newPractice?.id || null;
      }
    }

    // Create doctor profile with all the data
    const { error } = await supabase
      .from('doctor_profiles')
      .insert({
        auth_user_id: authUserId,
        full_name: profileData.fullName,
        medical_license: profileData.medicalLicense,
        license_expiry: profileData.licenseExpiry || null,
        specialization: profileData.specialization ? JSON.stringify(profileData.specialization) : null,
        primary_practice_id: practiceId,
        prescribing_enabled: false, // Requires admin verification
        require_2fa: false,
      });

    if (error) {
      // Doctor profile creation error handled
      throw new Error(error.message);
    }

    return { practiceId: practiceId || undefined };
  } catch (error) {
    // Doctor profile creation process error handled
    throw error;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<{ 
  authenticated: boolean; 
  error: Error | null;
}> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      // Authentication check error handled
      return { authenticated: false, error: new Error(error.message) };
    }

    return { authenticated: !!session, error: null };
  } catch (error) {
    // Unexpected authentication check error handled
    return { authenticated: false, error: error as Error };
  }
}